import Modal from '..';
import * as S from './styles';
import { useMemo, useState, useEffect } from 'react';
import { ImageElement } from 'dragonmouse-shared/devigner';
import { Attachment } from 'dragonmouse-shared/interfaces';
import Icon, { IconType } from 'components/Icon';
import { useTheme } from 'styled-components';
import { Loader } from 'components/Widget/Loader';
import { FileUploadCallbacks, uploadFile, UploadTracker, canUpload } from 'utils';
import { toast } from 'react-toastify';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import forge from 'apis/forge';

interface ImageSelectedModalProps {
	imageElement: ImageElement;
	onSubmit: (imageUrl: string, altTag: string) => void;
	onCancel: () => void;
}

type ImageSource = 'original' | 'uploaded' | 'generated' | 'file-pending';

const ImageSelectedModal = ({ imageElement, onSubmit, onCancel }: ImageSelectedModalProps) => {
	const [altTag, setAltTag] = useState(imageElement.altTag);
	const [imageSource, setImageSource] = useState<ImageSource>('original');
	const [candidateImageUrl, setCandidateImageUrl] = useState<string | null>(null); // storage path for uploaded images
	const [candidateImagePreview, setCandidateImagePreview] = useState<string | null>(null); // data URL for preview
	const [selectedFile, setSelectedFile] = useState<File | null>(null); // File object when user selects file (before upload)
	const [generatedImageData, setGeneratedImageData] = useState<string | null>(null); // base64 for generated images
	const [loadingAttachment, setLoadingAttachment] = useState<UploadTracker | null>(null);
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [originalDimensions, setOriginalDimensions] = useState<[number | null, number | null]>([
		null,
		null,
	]);
	const [isGenerating, setIsGenerating] = useState(false);
	const theme = useTheme();

	// Helper function to validate if a file is an image
	const isValidImageFile = (file: File): boolean => {
		if (file.type.startsWith('image/')) {
			return true;
		}
		// Also check file extension as a fallback (some browsers may not set MIME type correctly)
		const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico'];
		const fileName = file.name.toLowerCase();
		return validExtensions.some((ext) => fileName.endsWith(ext));
	};

	// Update altTag when imageElement changes
	useEffect(() => {
		setAltTag(imageElement.altTag);
	}, [imageElement.altTag]);

	// Capture image dimensions when modal loads
	useEffect(() => {
		const img = new Image();
		let cancelled = false;

		img.onload = () => {
			if (!cancelled) {
				setOriginalDimensions([img.naturalWidth, img.naturalHeight]);
			}
		};
		img.onerror = () => {
			if (!cancelled) {
				// If image fails to load, dimensions remain null
				setOriginalDimensions([null, null]);
			}
		};
		img.src = imageElement.imageUrl;

		// Cleanup: cancel the image loading if component unmounts or effect re-runs
		return () => {
			cancelled = true;
			// Remove event handlers to prevent callbacks from firing
			img.onload = null;
			img.onerror = null;
			// Clear src to abort loading
			img.src = '';
		};
	}, [imageElement.imageUrl]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	// File upload callbacks with functional setState
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				setLoadingAttachment(uploadTracker);
				setUploadError(null); // Clear any previous errors when starting a new upload
			},
			onLoadingEnd: (_uniqueId: string) => {
				setLoadingAttachment(null);
			},
			onSuccess: (attachment: Attachment) => {
				setCandidateImageUrl(attachment.storagePath);
				setSelectedFile(null); // Clear selected file after successful upload
				setImageSource('uploaded'); // Mark as uploaded
				setUploadError(null); // Clear error on success
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
				const errorMessage = error.message || 'Failed to upload image';
				setUploadError(errorMessage);
			},
		}),
		[]
	);

	// Handler that reads file for preview (but doesn't upload yet)
	const handleFileSelection = (file: File) => {
		// Clear any previous errors
		setUploadError(null);

		// Validate that the file is an image
		if (!isValidImageFile(file)) {
			const errorMessage = 'Please upload an image file (JPG, PNG, GIF, WebP, SVG, etc.)';
			setUploadError(errorMessage);
			return;
		}

		// Validate file size early for immediate feedback
		const validation = canUpload(file);
		if (!validation.canUpload) {
			setUploadError(validation.error || 'File size exceeds limit');
			return;
		}

		// Clear any previous image state when switching to file upload
		setGeneratedImageData(null);
		setCandidateImageUrl(null);
		setImageSource('file-pending');

		// Store the file for later upload
		setSelectedFile(file);

		// Read file as data URL for image preview
		const previewReader = new FileReader();
		previewReader.onload = () => {
			setCandidateImagePreview(previewReader.result as string);
		};
		previewReader.onerror = () => {
			const errorMessage = 'Failed to read image file';
			setUploadError(errorMessage);
		};
		previewReader.readAsDataURL(file);
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	const onDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingFile(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			handleFileSelection(files[0]); // Only take first file
		}
	};

	// Handle image regeneration
	const handleRegenerate = async () => {
		if (!altTag.trim()) {
			toast.error('Please enter an alt tag to generate an image');
			return;
		}

		// Save the current state before generating
		const previousImageSource = imageSource;
		const previousGeneratedData = generatedImageData;
		const previousCandidateUrl = candidateImageUrl;
		const previousSelectedFile = selectedFile;
		const previousPreview = candidateImagePreview;

		setIsGenerating(true);
		setUploadError(null);

		// Clear any previous image state when switching to generation
		setSelectedFile(null);
		setCandidateImageUrl(null);
		setImageSource('generated');

		try {
			const response = await forge.generateImage({
				prompt: altTag,
				width: originalDimensions[0],
				height: originalDimensions[1],
			});

			// Convert base64 to data URL for preview
			const dataUrl = `data:image/png;base64,${response.imageData}`;
			setGeneratedImageData(response.imageData);
			setCandidateImagePreview(dataUrl);
		} catch (error) {
			console.error('Error generating image:', error);
			setUploadError('Failed to generate image. Please try again.');
			// Revert to previous state on error
			setImageSource(previousImageSource);
			setGeneratedImageData(previousGeneratedData);
			setCandidateImageUrl(previousCandidateUrl);
			setSelectedFile(previousSelectedFile);
			setCandidateImagePreview(previousPreview);
		} finally {
			setIsGenerating(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const onChooseFile = () => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) handleFileSelection(file);
		};
		input.click();
	};

	// Determine which image to display
	const displayImageUrl = candidateImagePreview || candidateImageUrl || imageElement.imageUrl;

	// Check if any changes have been made
	const hasChanges = imageSource !== 'original' || altTag !== imageElement.altTag;

	// Helper function to upload a file and return the storage path
	const uploadFileAndGetUrl = (file: File): Promise<string> => {
		return new Promise<string>((resolve, reject) => {
			const uploadCallbacks: FileUploadCallbacks = {
				onLoadingStart: fileCallbacks.onLoadingStart,
				onLoadingEnd: fileCallbacks.onLoadingEnd,
				onSuccess: (attachment: Attachment) => {
					fileCallbacks.onSuccess(attachment);
					resolve(attachment.storagePath);
				},
				onError: (error: Error) => {
					fileCallbacks.onError?.(error) ?? toast.error('Failed to upload image');
					reject(error);
				},
			};
			uploadFile(file, uploadCallbacks).catch(reject);
		});
	};

	const handleSubmit = async () => {
		let uploadedImageUrl: string | null = null;

		// Handle different image sources
		if (imageSource === 'generated' && generatedImageData) {
			// Convert base64 data to File object and use the same uploadFile process
			try {
				// Convert base64 to blob, then to File
				const byteCharacters = atob(generatedImageData);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: 'image/png' });
				const generatedFile = new File([blob], 'generated-image.png', { type: 'image/png' });

				uploadedImageUrl = await uploadFileAndGetUrl(generatedFile);
			} catch (error) {
				// Upload failed, error already handled by onError callback
				return; // Don't submit if upload failed
			}
		} else if (imageSource === 'file-pending' && selectedFile) {
			// Upload selected file before submitting
			try {
				uploadedImageUrl = await uploadFileAndGetUrl(selectedFile);
			} catch (error) {
				// Upload failed, error already handled by onError callback
				return; // Don't submit if upload failed
			}
		} else if (imageSource === 'uploaded' && candidateImageUrl) {
			// Already uploaded, use existing URL
			uploadedImageUrl = candidateImageUrl;
		}

		const finalImageUrl = uploadedImageUrl || imageElement.imageUrl;
		onSubmit(finalImageUrl, altTag);
	};

	const renderDropZone = () => {
		if (displayImageUrl) {
			return (
				<S.DropZone onClick={onChooseFile} backgroundImage={displayImageUrl}>
					{loadingAttachment && (
						<S.DragOverlay>
							<S.DragOverlayContent>
								<Loader width={24} height={24} />
								<S.DragOverlayText>Uploading image...</S.DragOverlayText>
							</S.DragOverlayContent>
						</S.DragOverlay>
					)}
				</S.DropZone>
			);
		}
		if (loadingAttachment) {
			return (
				<S.DropZone onClick={onChooseFile}>
					<S.DropZoneLoading>
						<Loader width={24} height={24} />
						<S.DropZoneLoadingText>Uploading image...</S.DropZoneLoadingText>
					</S.DropZoneLoading>
				</S.DropZone>
			);
		}
		return (
			<S.DropZone onClick={onChooseFile}>
				<S.DropZoneTitle>Drop image here or click to choose</S.DropZoneTitle>
			</S.DropZone>
		);
	};

	return (
		<Modal
			title={'Update Image'}
			cancelLabel="Cancel"
			submitLabel="Update"
			disabled={!hasChanges || isGenerating}
			loading={loadingAttachment !== null}
			cancelDisabled={loadingAttachment !== null || isGenerating}
			onCancel={onCancel}
			onSubmit={handleSubmit}
		>
			<S.Content
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				onPaste={(e) => {
					const items = e.clipboardData?.items;
					if (!items) return;

					for (const item of items) {
						if (item.kind === 'file') {
							const file = item.getAsFile();
							if (file) {
								handleFileSelection(file);
								e.preventDefault();
								return;
							}
						}
					}
				}}
			>
				{renderDropZone()}
				{isDraggingFile && (
					<S.DragOverlay>
						<S.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<S.DragOverlayText>Drop file to upload</S.DragOverlayText>
						</S.DragOverlayContent>
					</S.DragOverlay>
				)}
			</S.Content>
			<MultilineInput
				value={altTag}
				onChange={(e) => setAltTag(e.target.value)}
				placeholder="Enter prompt for image generation"
				minLines={1}
			/>
			<S.ButtonContainer>
				<S.ActionButton
					onClick={onChooseFile}
					disabled={isGenerating || loadingAttachment !== null}
				>
					<Icon type={IconType.ATTACHMENT} width={16} height={16} />
					Upload Image
				</S.ActionButton>
				<S.ActionButton
					onClick={handleRegenerate}
					disabled={isGenerating || loadingAttachment !== null || !altTag.trim()}
				>
					{isGenerating ? (
						<>
							<Loader width={16} height={16} />
							Generating...
						</>
					) : (
						<>
							<Icon type={IconType.REFRESH} width={16} height={16} />
							Regenerate
						</>
					)}
				</S.ActionButton>
			</S.ButtonContainer>
			{uploadError && <S.ErrorMessage>{uploadError}</S.ErrorMessage>}
		</Modal>
	);
};

export default ImageSelectedModal;
